import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { json } from '../api/apiClient';

const ThemeContext = createContext(null);

/**
 * Löst das tatsächliche Theme auf Basis des gespeicherten Werts und
 * der OS-Präferenz auf ('system' → dark/light je nach matchMedia).
 */
function resolveTheme(theme) {
  if (theme === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return theme === 'light' ? 'light' : 'dark';
}

export function ThemeProvider({ children }) {
  // Startwert: zuerst localStorage prüfen, Fallback 'system'
  const [theme, setThemeState] = useState(
    () => localStorage.getItem('theme') ?? 'system'
  );
  const [resolvedTheme, setResolvedTheme] = useState(() => resolveTheme(
    localStorage.getItem('theme') ?? 'system'
  ));

  // Debounce-Timer-Ref für API-Calls
  const saveTimerRef = useRef(null);

  // Zeitweiliges Theme (z. B. dunkles Brett); wird weder gespeichert noch an den Server gesendet
  const [themeOverride, setThemeOverrideState] = useState(null);

  // Wende Override bzw. resolvedTheme auf <html> an
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', themeOverride ?? resolvedTheme);
  }, [resolvedTheme, themeOverride]);

  const setThemeOverride = useCallback((value) => setThemeOverrideState(value), []);
  const clearThemeOverride = useCallback(() => setThemeOverrideState(null), []);

  // OS-Präferenz-Listener (nur aktiv wenn theme === 'system')
  useEffect(() => {
    if (theme !== 'system') return;

    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setResolvedTheme(e.matches ? 'dark' : 'light');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [theme]);

  /**
   * Theme setzen: sofort anwenden + localStorage + debounced API-Speicherung
   */
  const setTheme = useCallback((newTheme) => {
    const resolved = resolveTheme(newTheme);
    setThemeState(newTheme);
    setResolvedTheme(resolved);
    localStorage.setItem('theme', newTheme);

    // Debounced PATCH an /api/me/preferences
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) return; // nicht eingeloggt – nur localStorage
        await json('/api/me/preferences', {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ theme: newTheme }),
        });
      } catch (err) {
        // Stille Fehler – Theme ist bereits lokal korrekt gesetzt
        console.error('[ThemeContext] Fehler beim Speichern des Themes:', err);
      }
    }, 800);
  }, []);

  /**
   * Beim Login: Gespeichertes Theme aus den User-Preferences laden.
   * Wird von AuthContext beim erfolgreichen Login aufgerufen.
   */
  const loadThemeFromPreferences = useCallback(async () => {
    try {
      const data = await json('/api/me/preferences');
      const savedTheme = data?.preferences?.theme;
      if (savedTheme && ['system', 'light', 'dark'].includes(savedTheme)) {
        setThemeState(savedTheme);
        setResolvedTheme(resolveTheme(savedTheme));
        localStorage.setItem('theme', savedTheme);
      }
    } catch (err) {
      // Kein Problem – localStorage-Wert bleibt aktiv
      console.error('[ThemeContext] Fehler beim Laden des Themes:', err);
    }
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, setTheme, loadThemeFromPreferences, setThemeOverride, clearThemeOverride }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme muss innerhalb von <ThemeProvider> verwendet werden');
  return ctx;
}
